import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

/** Stavebni prvky administrace. Tridy jsou z design/prototype.html. */

const ICON_PATHS = {
  home: 'M3 11l9-8 9 8M5 9.5V20h14V9.5M10 20v-6h4v6',
  flag: 'M5 21V4M5 4h12l-2.5 4L17 12H5',
  route:
    'M6 19a2 2 0 100-4 2 2 0 000 4zM18 9a2 2 0 100-4 2 2 0 000 4zM8 17h7a3 3 0 000-6H9a3 3 0 010-6h7',
  users:
    'M16 20v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1M9.5 11a4 4 0 100-8 4 4 0 000 8zM21 20v-1a4 4 0 00-3-3.9M16 3.1a4 4 0 010 7.8',
  mail: 'M3 5h18v14H3zM3 7l9 6 9-6',
  star: 'M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2l-5.6 3 1.1-6.2L3 9.6l6.2-.9z',
  alert: 'M12 3l10 18H2zM12 10v5M12 18v.01',
  chart: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  database:
    'M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  settings: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4',
  flask: 'M9 3h6M10 3v6l-6 10a2 2 0 002 3h12a2 2 0 002-3l-6-10V3',
  menu: 'M4 6h16M4 12h16M4 18h16',
  close: 'M6 6l12 12M18 6L6 18',
  chevron: 'M9 6l6 6-6 6',
  down: 'M6 9l6 6 6-6',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  upload: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  send: 'M21 3L3 10.5l7 3 3 7zM10 13.5L21 3',
  bolt: 'M13 2L4 14h7l-1 8 9-12h-7z',
  clock: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 7v5l3 2',
  layers: 'M12 3l9 5-9 5-9-5zM3 13l9 5 9-5',
  flow: 'M6 5a2 2 0 100 .01M18 19a2 2 0 100 .01M6 7v4a3 3 0 003 3h6a3 3 0 013 3v0',
  file: 'M14 3H6v18h12V7zM14 3v4h4M9 12h6M9 16h6',
  segment: 'M12 12m-9 0a9 9 0 1018 0 9 9 0 10-18 0M12 12V3M12 12l7.8 4.5',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 01-12 0zM12 17v4',
  info: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v5M12 8v.01',
  refresh: 'M20 11a8 8 0 00-14.5-3.5L4 9M4 4v5h5M4 13a8 8 0 0014.5 3.5L20 15M20 20v-5h-5',
  back: 'M15 18l-6-6 6-6',
  inbox: 'M3 13l3-8h12l3 8v6H3zM3 13h5l1 3h6l1-3h5',
  up: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  downtrend: 'M3 7l6 6 4-4 8 8M15 17h6v-6',
} as const;

export type IconName = keyof typeof ICON_PATHS;

/** Maly sada ikon (24x24, caru podle currentColor). Zadna zavislost. */
export function Icon({
  name,
  size = 18,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}): JSX.Element {
  return (
    <svg
      className={`icon${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

export function Panel({
  title,
  action,
  children,
  className,
  icon,
  subtitle,
}: {
  title?: string;
  action?: ReactNode;
  icon?: IconName;
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={`panel ${className ?? ''}`}>
      {(title || action) && (
        <div className="panel-head">
          <div className="panel-title">
            {icon ? <Icon name={icon} className="panel-ic" /> : null}
            <div>
              {title ? <h2>{title}</h2> : null}
              {subtitle ? <p className="panel-sub">{subtitle}</p> : null}
            </div>
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export type Tone = 'ok' | 'warn' | 'bad' | 'neu' | 'acc' | 'sim';

/** Stav vzdy s ikonou a textem, nikdy jen barvou (UI.md). */
export function Pill({ tone, children }: { tone: Tone; children: ReactNode }): JSX.Element {
  return <span className={`pill ${tone}`}>{children}</span>;
}

export function Tile({
  label,
  value,
  note,
  bad,
  icon,
  trend,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  bad?: boolean;
  /** Ikona v rohu dlazdice. */
  icon?: IconName;
  /** Zmena oproti minulemu obdobi, napr. "+12 %"; tone urcuje barvu. */
  trend?: { text: string; tone?: 'ok' | 'bad' | 'neu' };
}): JSX.Element {
  return (
    <div className={`tile${bad ? ' badt' : ''}`}>
      <div className="tile-top">
        <small>{label}</small>
        {icon ? (
          <span className="tile-ic">
            <Icon name={icon} size={16} />
          </span>
        ) : null}
      </div>
      <b>{value}</b>
      {trend ? <span className={`trend ${trend.tone ?? 'neu'}`}>{trend.text}</span> : null}
      {note ? <em>{note}</em> : null}
    </div>
  );
}

export function Loading({ what }: { what?: string }): JSX.Element {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>Načítám {what ?? 'data'}…</span>
    </div>
  );
}

/** Nový název pro stejný prvek (viz docs/UI-KOMPONENTY.md). */
export const LoadingState = Loading;

/**
 * Chybový stav: co se stalo (lidsky) + volitelné „Zkusit znovu“.
 * `title` říká, co se nepovedlo; zpráva z chyby jde pod něj.
 */
export function ErrorState({
  error,
  title,
  onRetry,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
}): JSX.Element {
  const message = error instanceof Error ? error.message : 'Neznámá chyba.';
  return (
    <div className="confirm dngc errstate" role="alert">
      <b>
        <Icon name="alert" size={16} /> {title ?? 'Nepovedlo se'}
      </b>
      <span>{message}</span>
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          <Icon name="refresh" size={14} /> Zkusit znovu
        </button>
      ) : null}
    </div>
  );
}

/** Zpětně kompatibilní název; nové obrazovky používají ErrorState. */
export function ErrorNote({ error }: { error: unknown }): JSX.Element {
  return <ErrorState error={error} />;
}

/** Prázdný stav: řekne, proč je tu prázdno, a (když to jde) nabídne další krok. */
export function EmptyState({
  title,
  children,
  action,
  icon,
}: {
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  icon?: IconName;
}): JSX.Element {
  return (
    <div className="empty">
      <Icon name={icon ?? 'inbox'} size={22} />
      {title ? <b>{title}</b> : null}
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <EmptyState>{children}</EmptyState>;
}

/** Stav s ikonou i textem (nikdy jen barvou); `icon` je volitelná ikona před textem. */
export function StatusBadge({
  tone,
  icon,
  children,
}: {
  tone: Tone;
  icon?: IconName;
  children: ReactNode;
}): JSX.Element {
  return (
    <span className={`pill ${tone}`}>
      {icon ? <Icon name={icon} size={12} /> : null}
      {children}
    </span>
  );
}

/**
 * Hlavička stránky: nadpis, věta „k čemu stránka slouží“ a akce vpravo.
 * Na stránce je právě jedno `level={1}`; vnořený přehled použije `level={2}`.
 */
export function PageHeader({
  title,
  description,
  actions,
  level = 1,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  level?: 1 | 2;
}): JSX.Element {
  const H = level === 1 ? 'h1' : 'h2';
  return (
    <div className="head page-head">
      <div>
        <H>{title}</H>
        {description ? <p className="lede">{description}</p> : null}
      </div>
      {actions ? <div className="filters">{actions}</div> : null}
    </div>
  );
}

/** Pole formuláře: popisek, nápověda a chyba jsou součástí jednoho `<label>`. */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * Modální okno nad nativním `<dialog>`: focus zůstává uvnitř, Esc a klik mimo zavře,
 * po zavření se focus vrátí na tlačítko, které okno otevřelo.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  wide,
  full,
  beforeClose,
  role,
  testId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
  /** Okno přes celou šířku se scrollem uvnitř (editory). */
  full?: boolean;
  /** Vrací `false`, když se zavření má zrušit (např. neuložené změny); platí pro Esc, křížek i klik vedle. */
  beforeClose?: () => boolean;
  role?: 'dialog' | 'alertdialog';
  testId?: string;
}): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`dlg${wide ? ' wide' : ''}${full ? ' full' : ''}`}
      role={role}
      aria-label={title}
      data-testid={testId}
      onClose={onClose}
      onCancel={(e) => {
        if (beforeClose && !beforeClose()) e.preventDefault();
      }}
      onClick={(e) => {
        if (e.target === ref.current && !full && (!beforeClose || beforeClose())) onClose();
      }}
    >
      {open ? (
        <div className="dlg-body">
          <div className="dlg-head">
            <h2>{title}</h2>
            <button
              type="button"
              className="btn iconbtn"
              aria-label="Zavřít"
              onClick={() => {
                if (!beforeClose || beforeClose()) onClose();
              }}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
          {children}
        </div>
      ) : null}
    </dialog>
  );
}

/**
 * Potvrzovací dialog pro nebezpečné akce (modální, na rozdíl od `Confirm`, který je v řádku).
 * `requireWord` vynutí opsání slova; tlačítko Zrušit má výchozí focus.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger,
  requireWord,
  busy,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireWord?: string;
  busy?: boolean;
  /** Další podmínka potvrzení mimo opsané slovo (např. povinný důvod ve formuláři v dialogu). */
  confirmDisabled?: boolean;
  onConfirm: (word?: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [word, setWord] = useState('');
  /* Slovo se porovnává bez diakritiky a bez ohledu na velikost, aby šlo opsat i na cizí klávesnici. */
  const plain = (v: string): string =>
    v
      .trim()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toUpperCase();
  const ok = !requireWord || plain(word) === plain(requireWord);
  const close = (): void => {
    setWord('');
    onCancel();
  };
  return (
    <Dialog open={open} onClose={close} title={title} role="alertdialog">
      <div className="dlg-content">{children}</div>
      {requireWord ? (
        <Field label={`Pro potvrzení napište slovo ${requireWord}`}>
          <input type="text" value={word} onChange={(e) => setWord(e.target.value)} />
        </Field>
      ) : null}
      <div className="row dlg-actions">
        <button type="button" className="btn" autoFocus onClick={close}>
          Zrušit
        </button>
        <button
          type="button"
          className={`btn ${danger ? 'dng' : 'pri'}`}
          disabled={!ok || busy || confirmDisabled}
          onClick={() => onConfirm(requireWord ? word : undefined)}
        >
          {confirmLabel ?? 'Potvrdit'}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * Potvrzeni v ramci stranky, ne `window.confirm` (UI.md: akce, ktere meni
 * data, maji potvrzeni v ramci stranky).
 */
export function Confirm({
  label,
  question,
  confirmLabel,
  danger,
  requireWord,
  disabled,
  disabledReason,
  onConfirm,
  busy,
}: {
  label: string;
  question: string;
  confirmLabel?: string;
  danger?: boolean;
  /** Akce vyzaduje napsat presne toto slovo (napr. OSTRÝ). */
  requireWord?: string;
  disabled?: boolean;
  disabledReason?: string;
  onConfirm: (word?: string) => void | Promise<void>;
  busy?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');

  if (disabled) {
    return (
      <span className="row">
        <button className="btn" disabled title={disabledReason}>
          {label}
        </button>
        {disabledReason ? <span className="note">{disabledReason}</span> : null}
      </span>
    );
  }

  if (!open) {
    return (
      <button className={`btn${danger ? ' dng' : ''}`} onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }

  const wordOk = !requireWord || word.trim().toUpperCase() === requireWord.toUpperCase();

  return (
    <div className={`confirm${danger ? ' dngc' : ''}`}>
      <b>{question}</b>
      {requireWord ? (
        <label className="row">
          <span>
            Potvrďte napsáním slova <code>{requireWord}</code>:
          </span>
          <input
            type="text"
            value={word}
            aria-label={`Potvrzení slovem ${requireWord}`}
            onChange={(e) => setWord(e.target.value)}
          />
        </label>
      ) : null}
      <div className="row">
        <button
          className={`btn ${danger ? 'dng' : 'pri'}`}
          disabled={!wordOk || busy}
          onClick={() => {
            void onConfirm(requireWord ? word : undefined);
            setOpen(false);
            setWord('');
          }}
        >
          {confirmLabel ?? 'Potvrdit'}
        </button>
        <button
          className="btn"
          onClick={() => {
            setOpen(false);
            setWord('');
          }}
        >
          Zrušit
        </button>
      </div>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="switch" title={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span />
    </label>
  );
}

export function Bar({ value, max }: { value: number; max: number }): JSX.Element {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <span className="bar" title={`${pct} %`}>
      <i style={{ width: `${pct}%` }} />
    </span>
  );
}

export function Toast({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="toast" role="status">
      {children}
    </div>
  );
}

/** Prehledne zalozky se strucnym popisem; aktivni je zvyraznena. */
export function TabBar<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ id: T; label: string; hint: string; icon?: IconName }>;
  value: T;
  onChange: (id: T) => void;
}): JSX.Element {
  return (
    <div className="tabbar" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          className={`tabbtn${value === t.id ? ' on' : ''}`}
          onClick={() => onChange(t.id)}
          data-testid={`tab-${t.id}`}
        >
          {t.icon ? <Icon name={t.icon} size={18} /> : null}
          <span>
            <b>{t.label}</b>
            <small>{t.hint}</small>
          </span>
        </button>
      ))}
    </div>
  );
}
